import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";

import "./BlockDeals.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";
import BlockDealsTSHistory from "../bdl-history/BlockDealsTSHistory";

const BDL_API_BASE_URL = "https://api.moneycontrol.com/mcapi/v1/deals/list";
const BDL_PAGE_SIZE = 1000;
const BDL_PAGE_OVERLAP = 200;
const BDL_PAGE_STEP = BDL_PAGE_SIZE - BDL_PAGE_OVERLAP;
const BDL_MAX_RECORDS = 10000;
const BDL_POLL_INTERVAL_MS = 5000;
const NSE_FNO_STOCK_LIST = [
    "COCHINSHIP", "ADANIENSOL", "HUDCO", "RBLBANK", "PNBHOUSING", "NMDC", "OFSS", "ADANIGREEN",
    "CHOLAFIN", "POLICYBZR", "POWERINDIA", "VOLTAS", "VBL", "COALINDIA", "SAIL", "TATAPOWER",
    "TRENT", "CUMMINSIND", "BRITANNIA", "TATATECH", "HDFCAMC", "COLPAL", "PPLPHARMA", "AUBANK",
    "POLYCAB", "NESTLEIND", "UNITDSPR", "PREMIERENE", "HINDALCO", "SBIN", "MARICO", "BANDHANBNK",
    "GAIL", "PIIND", "NYKAA", "BAJFINANCE", "PNB", "SUZLON", "FORTIS", "BAJAJ-AUTO", "EICHERMOT",
    "SHRIRAMFIN", "DALBHARAT", "AMBUJACEM", "GRASIM", "JINDALSTEL", "KOTAKBANK", "DELHIVERY",
    "HDFCBANK", "CANBK", "OIL", "BHEL", "LICI", "JSWSTEEL", "PFC", "NTPC", "NBCC", "JUBLFOOD",
    "DIVISLAB", "AXISBANK", "M&M", "SUPREMEIND", "BANKNIFTY", "TATASTEEL", "TMPV", "ASHOKLEY",
    "CONCOR", "DIXON", "KEI", "BSE", "MAZDOCK", "NATIONALUM", "SAMMAANCAP", "MFSL", "ONGC",
    "AMBER", "PHOENIXLTD", "ADANIENT", "INDIANB", "BPCL", "PIDILITIND", "ADANIPOWER", "HINDZINC",
    "PGEL", "APOLLOHOSP", "BLUESTARCO", "INDUSTOWER", "IDEA", "INDHOTEL", "RECLTD", "TVSMOTOR",
    "LTF", "INDIGO", "TORNTPHARM", "MAXHEALTH", "ASTRAL", "PAGEIND", "PRESTIGE", "BANKBARODA",
    "FEDERALBNK", "CDSL", "CIPLA", "MARUTI", "POWERGRID", "PETRONET", "TATACONSUM", "LICHSGFIN",
    "IDFCFIRSTB", "HINDPETRO", "APLAPOLLO", "YESBANK", "CNXMIDCAP", "TATAELXSI", "BHARATFORG",
    "DLF", "LT", "NHPC", "TITAN", "DRREDDY", "PAYTM", "MCX", "IRFC", "MANKIND", "ADANIPORTS",
    "BAJAJHLDNG", "NIFTY", "RELIANCE", "MANAPPURAM", "JIOFIN", "IREDA", "MOTHERSON", "360ONE",
    "ETERNAL", "GODREJPROP", "OBEROIRLTY", "ITC", "BAJAJFINSV", "BEL", "RVNL",
    "MOTILALOFS", "CAMS", "CROMPTON", "EXIDEIND", "PATANJALI", "LAURUSLABS", "IOC", "ULTRACEMCO",
    "HEROMOTOCO", "SIEMENS", "ASIANPAINT", "INDUSINDBK", "BHARTIARTL", "AUROPHARMA", "SBICARD",
    "UNIONBANK", "GLENMARK", "BDL", "ICICIBANK", "INOXWIND", "HINDUNILVR", "UPL", "WIPRO",
    "SONACOMS", "DMART", "HAVELLS", "CGPOWER", "HDFCLIFE", "KALYANKJIL", "LODHA", "BOSCHLTD",
    "MUTHOOTFIN", "SRF", "LUPIN", "DABUR", "FORCEMOT", "UNOMINDA", "HAL", "VEDL", "ZYDUSLIFE",
    "GMRAIRPORT", "SWIGGY", "SHREECEM", "ANGELONE", "ICICIGI", "GODFRYPHLP", "TORNTPOWER",
    "NUVAMA", "VMM", "BANKINDIA", "BIOCON", "ABCAPITAL", "KFINTECH", "KAYNES", "IEX",
    "JSWENERGY", "WAAREEENER", "SBILIFE", "NAUKRI", "ABB", "KPITTECH", "HYUNDAI", "SUNPHARMA",
    "TIINDIA", "ICICIPRULI", "SOLARINDS", "TECHM", "MPHASIS", "GODREJCP", "TCS", "NAM-INDIA",
    "ALKEM", "LTM", "COFORGE", "HCLTECH", "PERSISTENT", "INFY", "GVT&D"
];
const NSE_FNO_STOCK_SET = new Set(NSE_FNO_STOCK_LIST.map((symbol) => symbol.toUpperCase()));

const buildBdlDealsUrl = (start, limit) => {
    const params = new URLSearchParams({
        start: String(start),
        limit: String(limit),
        orderBy: "deal_date",
        sortBy: "DESC",
        dealType: "large",
        deviceType: "W",
        apiVersion: "177",
    });

    return `${BDL_API_BASE_URL}?${params.toString()}`;
};

const getBdlDealUniqueKey = (deal = {}) => JSON.stringify(deal);

const getDealSymbol = (deal = {}) => {
    const symbol = deal.sc_nseid?.trim() || deal.sc_comp?.trim() || "";
    return symbol.toUpperCase();
};

const getDealTimeNumber = (dealTime) => {
    const digits = String(dealTime ?? "").replace(/\D/g, "");
    if (!digits) return null;

    const hhmm = digits.length > 4
        ? digits.padStart(6, "0").slice(0, 4)
        : digits.padStart(4, "0");

    return Number(hhmm);
};

const fetchAllBdlDeals = async (passCount = 1) => {
    const allDeals = [];
    const seenDeals = new Set();

    for (let passIndex = 0; passIndex < passCount; passIndex += 1) {
        for (let start = 0; start < BDL_MAX_RECORDS; start += BDL_PAGE_STEP) {
            const response = await axios.get(buildBdlDealsUrl(start, BDL_PAGE_SIZE));
            const pageDeals =
                Array.isArray(response?.data?.data)
                    ? response.data.data
                    : [];

            if (pageDeals.length === 0) {
                break;
            }

            pageDeals.forEach((deal) => {
                const uniqueKey = getBdlDealUniqueKey(deal);

                if (!seenDeals.has(uniqueKey)) {
                    seenDeals.add(uniqueKey);
                    allDeals.push(deal);
                }
            });

            if (pageDeals.length < BDL_PAGE_SIZE) {
                break;
            }
        }
    }

    return allDeals;
};

export default function BlockDeals() {

    // WebSocket connection
    const { blockDealsLargeStockData, blockDealsTopStockData, stockSectorAnalysisData, stockAnalysisData }
        = useStockApiWebsocket(environment.stockApiDayRangeDataWSUrl);
    const [blockDealsLargeStockDataList, setblockDealsLargeStockDataList] = useState([]);
    const [blockDealsTopStockDataList, setblockDealsTopStockDataList] = useState([]);
    const [bdlDealsDataList, setBdlDealsDataList] = useState([]);
    const [bdlLoading, setBdlLoading] = useState(false);
    const [bdlError, setBdlError] = useState("");
    const [activeTab, setActiveTab] = useState("BDL_LS");
    const [bdlCountSortOrder, setBdlCountSortOrder] = useState("normal");
    const [stockSectorAnalysis, setStockSectorAnalysis] = useState([]);
    const [stockData, setStockData] = useState([]);
    /* ============================================== */
    const bdlPollInFlightRef = useRef(false);
    const hasLoadedInitialBdlDataRef = useRef(false);

    useEffect(() => {
        if (Array.isArray(blockDealsLargeStockData) && blockDealsLargeStockData.length > 0) {
            // ✅ Create stock lookup map
            const normalize = (val) =>
                val?.replace("NSE:", "").replace("-EQ", "").trim().toUpperCase();
            const stockMap = new Map(
                stockData.map((s) => [normalize(s.symbol), s])
            );

            // ✅ Merge stockData into BDL deals
            const enrichedBdlDeals = blockDealsLargeStockData.map((deal) => {
                const key = (deal.sc_nseid && deal.sc_nseid.trim() !== "")
                    ? normalize(deal.sc_nseid) : deal.sc_comp;
                const stock = stockMap.get(key.toUpperCase());

                if (stock) {
                    return {
                        ...deal,
                        lp: stock.lp,
                        high_price: stock.high_price,
                        low_price: stock.low_price,
                        open_price: stock.open_price,
                    };
                }

                return deal;
            });

            setblockDealsLargeStockDataList(enrichedBdlDeals);
            console.log('blockDealsLargeStockData enrichedBdlDeals => ', enrichedBdlDeals);
        }
    }, [blockDealsLargeStockData]);

    useEffect(() => {
        if (Array.isArray(blockDealsTopStockData) && blockDealsTopStockData.length > 0) {
            // ✅ Create stock lookup map
            const normalize = (val) =>
                val?.replace("NSE:", "").replace("-EQ", "").trim().toUpperCase();
            const stockMap = new Map(
                stockData.map((s) => [normalize(s.symbol), s])
            );

            // ✅ Merge stockData into BDL deals
            const enrichedBdlDeals = blockDealsTopStockData.map((deal) => {
                const key = (deal.sc_nseid && deal.sc_nseid.trim() !== "")
                    ? normalize(deal.sc_nseid) : deal.sc_comp;
                const stock = stockMap.get(key.toUpperCase());

                if (stock) {
                    return {
                        ...deal,
                        lp: stock.lp,
                        high_price: stock.high_price,
                        low_price: stock.low_price,
                        open_price: stock.open_price,
                    };
                }

                return deal;
            });
            setblockDealsTopStockDataList(enrichedBdlDeals);
        }
    }, [blockDealsTopStockData]);

    useEffect(() => {
        if (Array.isArray(stockSectorAnalysisData) && stockSectorAnalysisData.length > 0) {
            setStockSectorAnalysis(stockSectorAnalysisData);

        }
    }, [stockSectorAnalysisData]);

    useEffect(() => {
        if (Array.isArray(stockAnalysisData) && stockAnalysisData.length > 0) {
            setStockData(stockAnalysisData);
            console.log(stockAnalysisData);
        }
    }, [stockAnalysisData]);

    // 🔥 MAIN MERGED LOGIC
    useEffect(() => {
        let isMounted = true;

        const normalize = (val) =>
            val?.replace("NSE:", "").replace("-EQ", "").trim().toUpperCase();

        const fetchBdlDealsData = async () => {
            if (bdlPollInFlightRef.current) return;

            bdlPollInFlightRef.current = true;
            if (isMounted) setBdlLoading(true);

            try {
                const latestBdlDeals = await fetchAllBdlDeals(
                    hasLoadedInitialBdlDataRef.current ? 1 : 2
                );

                const filteredBdlDeals = latestBdlDeals.filter((deal) => {
                    const exchange =
                        typeof deal?.exchange === "string" ? deal.exchange.trim().toUpperCase() : "";
                    const dealValue = Number(deal?.dealValue);
                    const tradedPrice = Number(deal?.tradedPrice);
                    const symbol =
                        typeof deal?.sc_nseid === "string" && deal.sc_nseid.trim() !== ""
                            ? deal.sc_nseid.trim().toUpperCase()
                            : typeof deal?.sc_comp === "string"
                                ? deal.sc_comp.trim().toUpperCase()
                                : "";

                    return (
                        exchange === "NSE" &&
                        dealValue >= 3 &&
                        tradedPrice >= 200 &&
                        symbol !== "" &&
                        NSE_FNO_STOCK_SET.has(symbol)
                    );
                });

                // ✅ Create stock lookup map
                const stockMap = new Map(
                    stockData.map((s) => [normalize(s.symbol), s])
                );

                // ✅ Merge stockData into BDL deals
                const enrichedBdlDeals = filteredBdlDeals.map((deal) => {
                    const key = (deal.sc_nseid && deal.sc_nseid.trim() !== "")
                        ? normalize(deal.sc_nseid) : deal.sc_comp;
                    const stock = stockMap.get(key.toUpperCase());

                    if (stock) {
                        return {
                            ...deal,
                            lp: stock.lp,
                            high_price: stock.high_price,
                            low_price: stock.low_price,
                            open_price: stock.open_price,
                        };
                    }

                    return deal;
                });

                if (!isMounted) return;

                console.log("Enriched BDL Deals:", enrichedBdlDeals);

                // ✅ FINAL DATA SET
                setBdlDealsDataList(enrichedBdlDeals);
                setBdlError("");
                hasLoadedInitialBdlDataRef.current = true;

            } catch (error) {
                console.error("Error fetching BDL data:", error);

                if (!isMounted) return;

                setBdlError("Unable to load BDL data.");
            } finally {
                bdlPollInFlightRef.current = false;

                if (isMounted) setBdlLoading(false);
            }
        };

        fetchBdlDealsData();

        const pollIntervalId = window.setInterval(fetchBdlDealsData, BDL_POLL_INTERVAL_MS);

        return () => {
            isMounted = false;
            bdlPollInFlightRef.current = false;
            window.clearInterval(pollIntervalId);
        };

    }, [stockData]); // 🔥 IMPORTANT dependency
    const getTopAndBottomCHP = (data) => {
        if (!Array.isArray(data) || data.length < 2) return {};

        // Sort by CHP ascending
        const sorted = [...data].sort((a, b) => a.chp - b.chp);

        return {
            firstLowest: sorted[0],
            secondLowest: sorted[1],
            thirdLowest: sorted[2],

            firstHighest: sorted[sorted.length - 1],
            secondHighest: sorted[sorted.length - 2],
            thirdHighest: sorted[sorted.length - 3],
        };
    };

    const formatDealTime = (value) => {
        if (!value) return "--";

        const formattedValue = String(value).padStart(4, "0");
        return `${formattedValue.slice(0, 2)}:${formattedValue.slice(2)}`;
    };

    // Header Data
    const headerSource =
        //activeTab === "BDL" ? (bdlDealsDataList?.[0] ?? {}): 

        (blockDealsLargeStockDataList?.[0] ?? {});
    const headerData = [
        {
            label1: "1st HS",
            value1: getTopAndBottomCHP(stockSectorAnalysis)?.firstHighest?.symbol?.split(":")[1]?.split("-")[0] ?? "--",
            label2: "CHP",
            value2: getTopAndBottomCHP(stockSectorAnalysis)?.firstHighest?.chp ?? "--",
        },
        {
            label1: "2nd HS",
            value1: getTopAndBottomCHP(stockSectorAnalysis)?.secondHighest?.symbol?.split(":")[1]?.split("-")[0] ?? "--",
            label2: "CHP",
            value2: getTopAndBottomCHP(stockSectorAnalysis)?.secondHighest?.chp ?? "--",
        },
        {
            label1: "3rd HS",
            value1: getTopAndBottomCHP(stockSectorAnalysis)?.thirdHighest?.symbol?.split(":")[1]?.split("-")[0] ?? "--",
            label2: "CHP",
            value2: getTopAndBottomCHP(stockSectorAnalysis)?.thirdHighest?.chp ?? "--",
        },
        {
            label1: "1st LS",
            value1: getTopAndBottomCHP(stockSectorAnalysis)?.firstLowest?.symbol?.split(":")[1]?.split("-")[0] ?? "--",
            label2: "CHP",
            value2: getTopAndBottomCHP(stockSectorAnalysis)?.firstLowest?.chp ?? "--",
        },
        {
            label1: "2nd LS",
            value1: getTopAndBottomCHP(stockSectorAnalysis)?.secondLowest?.symbol?.split(":")[1]?.split("-")[0] ?? "--",
            label2: "CHP",
            value2: getTopAndBottomCHP(stockSectorAnalysis)?.secondLowest?.chp ?? "--",
        },
        {
            label1: "3rd LS",
            value1: getTopAndBottomCHP(stockSectorAnalysis)?.thirdLowest?.symbol?.split(":")[1]?.split("-")[0] ?? "--",
            label2: "CHP",
            value2: getTopAndBottomCHP(stockSectorAnalysis)?.thirdLowest?.chp ?? "--",
        },
        {
            label1: "Date",
            value1:
                //activeTab === "BDL" ? (headerSource?.deal_date ?? "--") : 
                (headerSource?.localDate ?? "--"),
            label2: "Time",
            value2: //activeTab === "BDL" ? formatDealTime(headerSource?.deal_time) : 
                (headerSource?.localTime ?? "--"),
        },
    ];

    const formatValue = (value) => {
        if (value == null || isNaN(value)) return "--";

        const num = Number(value);
        const absNum = Math.abs(num);
        const sign = num < 0 ? "-" : "";


        // 🔥 Whole number (no decimal)
        if (Number.isInteger(num)) {
            return `${sign}${absNum.toLocaleString("en-IN")}`;
        }

        // 🔥 Default (Indian format with 2 decimal)
        return `${sign}${absNum.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const getChpColor = (value) => {
        const num = Number(value);
        if (isNaN(num)) return "white";
        if (num > 0) return "#0dc80d";   // green
        if (num < 0) return "rgb(228 67 67)";   // red
        return "white";
    };
    const leftData = headerData.filter(item => item.label1 !== "Date");
    const rightData = headerData.find(item => item.label1 === "Date");

    const getLtpColor = (row) => {
        const { lp, open_price, high_price, low_price, tradedPrice } = row;

        if (lp < open_price && lp < high_price && lp < tradedPrice) {
            return "ltp-red";
        }

        if (lp > open_price && lp > low_price && lp > tradedPrice) {
            return "ltp-green";
        }

        return "inherit";
    };

    const bdlSelectDataList = useMemo(() => {
        if (!Array.isArray(bdlDealsDataList) || bdlDealsDataList.length === 0) return [];

        // The API is requested newest first, so the first row identifies the active trading date.
        const selectedDate = bdlDealsDataList[0]?.deal_date;
        const dealsForSelectedDate = bdlDealsDataList.filter(
            (deal) => deal?.deal_date === selectedDate
        );
        const openingDealsBySymbol = new Map();
        const afterOpeningDealsBySymbol = new Map();

        dealsForSelectedDate.forEach((deal) => {
            const symbol = getDealSymbol(deal);
            const dealTime = getDealTimeNumber(deal.deal_time);
            const tradedPrice = Number(deal.tradedPrice);

            if (!symbol || dealTime === null) return;

            if (dealTime >= 915 && dealTime <= 925) {
                const existing = openingDealsBySymbol.get(symbol);
                const hasValidPrice = Number.isFinite(tradedPrice);

                if (!existing) {
                    openingDealsBySymbol.set(symbol, {
                        ...deal,
                        selectionType: "09:15 - 09:25 AVG",
                        priceTotal: hasValidPrice ? tradedPrice : 0,
                        priceCount: hasValidPrice ? 1 : 0,
                        dealsCount: 1,
                    });
                } else {
                    const priceTotal = existing.priceTotal + (hasValidPrice ? tradedPrice : 0);
                    const priceCount = existing.priceCount + (hasValidPrice ? 1 : 0);
                    const dealsCount = existing.dealsCount + 1;
                    const existingTime = getDealTimeNumber(existing.deal_time);
                    const isEarlierDeal = existingTime === null || dealTime < existingTime;

                    if (isEarlierDeal) {
                        openingDealsBySymbol.set(symbol, {
                            ...deal,
                            selectionType: "09:15 - 09:25 AVG",
                            priceTotal,
                            priceCount,
                            dealsCount,
                        });
                        return;
                    }

                    existing.priceTotal = priceTotal;
                    existing.priceCount = priceCount;
                    existing.dealsCount = dealsCount;
                }
                return;
            }

            if (dealTime > 925) {
                const existing = afterOpeningDealsBySymbol.get(symbol);
                const existingTime = getDealTimeNumber(existing?.deal_time);

                if (!existing || existingTime === null || dealTime < existingTime) {
                    afterOpeningDealsBySymbol.set(symbol, {
                        ...deal,
                        selectionType: "AFTER 09:25",
                        dealsCount: 1,
                    });
                }
            }
        });

        const openingRows = Array.from(openingDealsBySymbol.values()).map((deal) => ({
            ...deal,
            averageTradedPrice: deal.priceCount > 0
                ? deal.priceTotal / deal.priceCount
                : null,
        }));

        // A symbol is displayed only once. The opening-window aggregate takes priority.
        openingRows.forEach((deal) => {
            afterOpeningDealsBySymbol.delete(getDealSymbol(deal));
        });

        return [...openingRows, ...afterOpeningDealsBySymbol.values()].sort((first, second) => {
            const firstTime = getDealTimeNumber(first.deal_time) ?? Number.MAX_SAFE_INTEGER;
            const secondTime = getDealTimeNumber(second.deal_time) ?? Number.MAX_SAFE_INTEGER;

            if (firstTime !== secondTime) return firstTime - secondTime;

            return getDealSymbol(first).localeCompare(getDealSymbol(second));
        });
    }, [bdlDealsDataList]);

    const displayedBdlSelectDataList = useMemo(() => {
        if (bdlCountSortOrder === "normal") return bdlSelectDataList;

        const direction = bdlCountSortOrder === "ascending" ? 1 : -1;

        return [...bdlSelectDataList].sort((first, second) => {
            const countDifference = (first.dealsCount - second.dealsCount) * direction;
            if (countDifference !== 0) return countDifference;

            const firstTime = getDealTimeNumber(first.deal_time) ?? Number.MAX_SAFE_INTEGER;
            const secondTime = getDealTimeNumber(second.deal_time) ?? Number.MAX_SAFE_INTEGER;
            return firstTime - secondTime;
        });
    }, [bdlSelectDataList, bdlCountSortOrder]);

    const cycleBdlCountSortOrder = () => {
        setBdlCountSortOrder((currentOrder) => {
            if (currentOrder === "normal") return "ascending";
            if (currentOrder === "ascending") return "descending";
            return "normal";
        });
    };

    return (
        <div className="block-deals-page">

            {/* ================= HEADER ================= */}
            <header className="header">
                <nav className="nav-container">
                    <div className="header-flex">

                        {/* ===== LEFT SIDE ===== */}
                        <div className="left-nav">

                            <div className="logo-container">
                                <img
                                    src="/assets/images/noptionwave-logo-1.png"
                                    className="header-logo"
                                    alt="logo"
                                />
                            </div>

                            {leftData.map((item, idx) => {
                                const color =
                                    item.label2 === "CHP"
                                        ? getChpColor(item.value2)
                                        : "white";

                                return (
                                    <div className="header-item" key={idx}>
                                        <span className="header-line" style={{ color }}>
                                            {item.label1} : {item.value1}
                                        </span>

                                        <span className="header-line" style={{ color }}>
                                            {item.label2} : {item.value2}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* ===== RIGHT SIDE (DATE & TIME) ===== */}
                        {rightData && (
                            <div className="right-nav">
                                <span className="header-line" style={{ color: "white" }}>
                                    {rightData.label1} : {rightData.value1}
                                </span>
                                <span className="header-line" style={{ color: "white" }}>
                                    {rightData.label2} : {rightData.value2}
                                </span>
                            </div>
                        )}

                    </div>
                </nav>
            </header>

            {/* ===== TABS ===== */}
            <div className="tab-container">
                <div className="tab">

                    {/* <button className={`tablinks sub-header-font ${activeTab === "BLOCK_DEALS_LARGE_STOCK" ? "active" : ""}`}
                        onClick={() => setActiveTab("BLOCK_DEALS_LARGE_STOCK")}  >
                        BLOCK DEALS LS
                    </button> */}

                    <button className={`tablinks sub-header-font ${activeTab === "BDL_LS" ? "active" : ""}`}
                        onClick={() => setActiveTab("BDL_LS")}  >
                        BDL LS
                    </button>

                    <button className={`tablinks sub-header-font ${activeTab === "BLOCK_DEALS_TOP_STOCK" ? "active" : ""}`}
                        onClick={() => setActiveTab("BLOCK_DEALS_TOP_STOCK")}  >
                        BDL TS
                    </button>

                    <button className={`tablinks sub-header-font ${activeTab === "BDL_SELECT" ? "active" : ""}`}
                        onClick={() => setActiveTab("BDL_SELECT")}>
                        BDL SELECT
                    </button>

                    <button className={`tablinks sub-header-font ${activeTab === "BDLTS_HISTORY" ? "active" : ""}`}
                        onClick={() => setActiveTab("BDLTS_HISTORY")}  >
                        BDLTS HISTORY
                    </button>

                </div>
            </div>

            {/* ================= GRID ================= */}

            {activeTab === "BDLTS_HISTORY" && (
                <div className="table-container">
                    <BlockDealsTSHistory />
                </div>
            )}

            {activeTab === "BDL_SELECT" && (
                <div className="table-container">
                    <table className="responsive-table text-center sticky-header">
                        <thead>
                            <tr>
                                <th>S.NO</th>
                                <th>DEAL DATE & TIME</th>
                                <th>SYMBOL</th>
                                <th>TD PRICE</th>
                                <th>AVG TD PRICE</th>
                                <th
                                    onClick={cycleBdlCountSortOrder}
                                    style={{ cursor: "pointer", userSelect: "none" }}
                                    title="Click to sort count ascending, descending, or return to normal time order"
                                >
                                    COUNT {bdlCountSortOrder === "ascending" ? "▲" : bdlCountSortOrder === "descending" ? "▼" : "↕"}
                                </th>
                                <th>OPEN</th>
                                <th>DH</th>
                                <th>LTP</th>
                                <th>DL</th>
                                <th>SECTOR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedBdlSelectDataList.length > 0 ? (
                                displayedBdlSelectDataList.map((row, index) => (
                                    <tr key={getDealSymbol(row)}>
                                        <td>{index + 1}</td>
                                        <td className="deal-cell">
                                            <div className="deal-row">
                                                <span>{row.deal_date ?? "--"}</span>
                                                <span className="span-color-price">
                                                    {formatDealTime(row.deal_time)}
                                                </span>
                                            </div>
                                        </td>
                                        <td><span className="span-color-price">{getDealSymbol(row) || "--"}</span></td>
                                        <td><span className="span-color-price">{formatValue(row.tradedPrice)}</span></td>
                                        <td><span className="span-color-price">{formatValue(row.averageTradedPrice)}</span></td>
                                        <td>{row.dealsCount}</td>
                                        <td>{row.open_price ?? "--"}</td>
                                        <td>{row.high_price ?? "--"}</td>
                                        <td className={getLtpColor(row)}>{row.lp ?? "--"}</td>
                                        <td>{row.low_price ?? "--"}</td>
                                        <td>{row.stock_sector ?? "--"}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="11">
                                        {bdlError || (bdlLoading ? "Loading BDL data..." : "No selected BDL data available.")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot className="fixed-footer">
                            <tr className="footer-left">
                                <td colSpan="11">noptionwave © 2026</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {activeTab === "BDL_LS" && (
                <div className="table-container">

                    <table className="responsive-table text-center sticky-header">
                        <thead>
                            <tr>
                                <th>DEAL DATE & TIME</th>
                                <th>SYMBOL</th>
                                <th>TRADED PRICE</th>
                                <th>OPEN</th>
                                <th>DH</th>
                                <th>LTP</th>
                                <th>DL</th>
                                <th>DEAL VALUE & QUANTITY</th>
                                <th>SECTOR</th>
                                {/* <th>VOLUME</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {bdlDealsDataList?.length > 0 ? (
                                bdlDealsDataList.map((row, idx) => (
                                    <tr key={idx}>
                                        <td className="deal-cell">

                                            <div className="deal-row">
                                                <span>{row.deal_date ?? "--"}</span>
                                                <span className="span-color-price">
                                                    {formatDealTime(row.deal_time)}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="span-color-price">
                                                {row.sc_nseid && row.sc_nseid.trim() !== ""
                                                    ? row.sc_nseid
                                                    : row.sc_comp || '--'}
                                            </span>
                                        </td>
                                        <td><span className="span-color-price">{formatValue(row.tradedPrice) ?? "--"}</span></td>
                                        <td><span>{row.open_price ?? "--"}</span></td>
                                        <td><span>{row.high_price ?? "--"}</span></td>
                                        <td className={getLtpColor(row)}>
                                            <span >  {row.lp ?? "--"} </span>
                                        </td>
                                        <td><span>{row.low_price ?? "--"}</span></td>
                                        <td className="deal-cell">
                                            <div className="deal-row">

                                                <span>Rs.</span>
                                                <span className="span-color">{row.dealValue ?? "--"} </span>

                                                <span>Crores</span>
                                                <span>{row.exchange ?? "--"} Block Trade For</span>
                                                <span className="span-color">{formatValue(row.quantity) ?? "--"}</span>
                                                <span>Shares</span>


                                            </div>
                                        </td>
                                        <td>{row.stock_sector ?? "--"}</td>
                                        {/* <td><span>{formatValue(row.volume) ?? "--"}</span></td> */}

                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="15">{bdlError || (bdlLoading ? "Loading BDL data..." : "No BDL data available.")}</td>
                                </tr>
                            )}
                        </tbody>

                        <tfoot className="fixed-footer">
                            <tr className="footer-left">
                                <td colSpan="15">noptionwave © 2026</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* ===== BLOCK DEALS ===== */}
            {activeTab === "BLOCK_DEALS_LARGE_STOCK" && (
                <div className="table-container">

                    <table className="responsive-table text-center sticky-header">
                        <thead>
                            <tr>
                                <th>DEAL DATE & TIME</th>
                                <th>SYMBOL</th>
                                <th>TRADED PRICE</th>
                                <th>OPEN</th>
                                <th>DH</th>
                                <th>LTP</th>
                                <th>DL</th>
                                <th>DEAL VALUE & QUANTITY</th>
                                <th>SECTOR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {blockDealsLargeStockDataList?.length > 0 ? (
                                blockDealsLargeStockDataList.map((row, idx) => (
                                    <tr key={idx}>
                                        <td className="deal-cell">

                                            <div className="deal-row">
                                                <span>{row.deal_date ?? "--"}</span>
                                                <span className="span-color-price">
                                                    {formatDealTime(row.deal_time)}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="span-color-price">
                                                {row.sc_nseid && row.sc_nseid.trim() !== ""
                                                    ? row.sc_nseid
                                                    : row.sc_comp || '--'}
                                            </span>
                                        </td>
                                        <td><span className="span-color-price">{formatValue(row.tradedPrice) ?? "--"}</span></td>
                                        <td><span>{row.open_price ?? "--"}</span></td>
                                        <td><span>{row.high_price ?? "--"}</span></td>
                                        <td className={getLtpColor(row)}>
                                            <span >  {row.lp ?? "--"} </span>
                                        </td>
                                        <td><span>{row.low_price ?? "--"}</span></td>
                                        <td className="deal-cell">
                                            <div className="deal-row">

                                                <span>Rs.</span>
                                                <span className="span-color">{row.dealValue ?? "--"} </span>

                                                <span>Crores</span>
                                                <span>NSE Block Trade For</span>
                                                <span className="span-color">{formatValue(row.quantity) ?? "--"}</span>
                                                <span>Shares</span>


                                            </div>
                                        </td>
                                        <td>{row.stock_sector ?? "--"}</td>
                                        {/* <td><span>{formatValue(row.volume) ?? "--"}</span></td> */}

                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="15">Waiting for live data...</td>
                                </tr>
                            )}
                        </tbody>

                        <tfoot className="fixed-footer">
                            <tr className="footer-left">
                                <td colSpan="15">noptionwave © 2026</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {activeTab === "BLOCK_DEALS_TOP_STOCK" && (
                <div className="table-container">

                    <table className="responsive-table text-center sticky-header">
                        <thead>
                            <tr>
                                <th>DEAL DATE</th>
                                <th>SYMBOL</th>
                                <th>TRADED PRICE</th>
                                <th>OPEN</th>
                                <th>DH</th>
                                <th>LTP</th>
                                <th>DL</th>
                                <th>DEALS COUNT</th>
                                <th>DEAL VALUE & QUANTITY</th>
                                <th>SECTOR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {blockDealsTopStockDataList?.length > 0 ? (
                                blockDealsTopStockDataList.map((row, idx) => (
                                    <tr key={idx}>
                                        <td className="deal-cell">

                                            <div className="deal-row">
                                                <span>{row.deal_date ?? "--"}</span>

                                            </div>
                                        </td>
                                        <td>
                                            <span className="span-color-price">
                                                {row.sc_nseid && row.sc_nseid.trim() !== ""
                                                    ? row.sc_nseid
                                                    : row.sc_comp || '--'}
                                            </span>
                                        </td>
                                        <td><span className="span-color-price">{formatValue(row.tradedPrice) ?? "--"}</span></td>
                                        <td><span>{row.open_price ?? "--"}</span></td>
                                        <td><span>{row.high_price ?? "--"}</span></td>
                                        <td className={getLtpColor(row)}>
                                            <span > {row.lp ?? "--"}  </span>
                                        </td>
                                        <td><span>{row.low_price ?? "--"}</span></td>
                                        <td><span>{row.dealsCount ?? "--"}</span></td>
                                        <td className="deal-cell">
                                            <div className="deal-row">

                                                <span>Rs.</span>
                                                <span className="span-color">{row.dealValue ?? "--"} </span>

                                                <span>Crores</span>
                                                <span>NSE Block Trade For</span>
                                                <span className="span-color">{formatValue(row.quantity) ?? "--"}</span>
                                                <span>Shares</span>


                                            </div>
                                        </td>
                                        <td>{row.stock_sector ?? "--"}</td>


                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="15">Waiting for live data...</td>
                                </tr>
                            )}
                        </tbody>

                        <tfoot className="fixed-footer">
                            <tr className="footer-left">
                                <td colSpan="15">noptionwave © 2026</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}


        </div>
    );
}
